/**
 * One coding agent's runtime health on each machine that offers it (ADR 0089, state.breakers and
 * state.selfTests): Healthy, Broken (the reason, since when), Testing, or Limited (its self-test
 * passed and one mission runs first). Under it the last self-test and the step it failed at, with
 * what Orgus read as the evidence. Test now asks the machine for a self-test; Turn off is the
 * agent's own switch, for an agent that stays broken.
 */
import { BREAKER_STATE_LABEL, SELF_TEST_STEP_LABEL, agentKindLabel } from '@orgus/protocol';
import type { AgentBreaker, Machine, SelfTestResult } from '@orgus/protocol';
import { Button, StatusPill, Tooltip, formatClock } from '@orgus/ui';
import type { PillStatus } from '@orgus/ui';

const PILL: Record<AgentBreaker['state'], PillStatus> = { open: 'failed', testing: 'working', 'half-open': 'queued' };

/** The line under the status: why, and since when. */
export function healthLine(b: AgentBreaker | null): string {
  if (!b) return 'Commands work';
  if (b.state === 'testing') return 'A self-test runs now';
  if (b.state === 'half-open') return 'Self-test passed, one mission runs first';
  return `${b.fault.label}, since ${formatClock(b.openedAt)}`;
}

/** "Self-test passed at 9:20 AM", "Self-test failed at 9:20 AM: create and commit a file". */
export function selfTestLine(t: SelfTestResult): string {
  return t.passed ? `Self-test passed at ${formatClock(t.at)}` : `Self-test failed at ${formatClock(t.at)}${t.failedStep ? `: ${SELF_TEST_STEP_LABEL[t.failedStep].toLowerCase()}` : ''}`;
}

/** What Orgus read for the step that failed (or the run's first step), for a tooltip. */
export function selfTestEvidence(t: SelfTestResult): string {
  const s = t.steps.find((x) => x.step === t.failedStep) ?? t.steps[0];
  return s ? s.evidence : 'No steps were recorded.';
}

export function AgentHealthRows({
  kind,
  machines,
  breakers,
  selfTests,
  showHealthy,
  busy,
  onTest,
  onTurnOff,
}: {
  kind: string;
  /** Machines that offer this kind. */
  machines: Pick<Machine, 'id' | 'name' | 'online'>[];
  breakers: readonly AgentBreaker[];
  selfTests: readonly SelfTestResult[];
  /** The breaker is on: a pair with nothing wrong says Healthy. */
  showHealthy: boolean;
  busy: boolean;
  onTest: (machineId: string) => void;
  onTurnOff: () => void;
}) {
  const label = agentKindLabel(kind);
  const rows = machines
    .map((m) => ({ m, b: breakers.find((x) => x.kind === kind && x.machineId === m.id) ?? null, t: selfTests.find((x) => x.kind === kind && x.machineId === m.id) ?? null }))
    .filter((r) => r.b || r.t || showHealthy);
  if (!rows.length) return null;
  return (
    <ul className="flex flex-col gap-2" aria-label={`${label} health by machine`}>
      {rows.map(({ m, b, t }) => (
        <li key={m.id} className="flex flex-col gap-1 text-caption">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill status={b ? PILL[b.state] : 'done'} label={b ? BREAKER_STATE_LABEL[b.state] : 'Healthy'} variant="plain" />
            <span className="text-fg-muted">{m.name}</span>
            <span className="min-w-0 truncate text-fg-subtle" title={b ? b.fault.excerpt : undefined}>
              {healthLine(b)}
            </span>
          </div>
          {t && (
            <Tooltip content={selfTestEvidence(t)}>
              <span tabIndex={0} className="w-fit text-fg-subtle">
                {selfTestLine(t)}
              </span>
            </Tooltip>
          )}
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" disabled={busy || b?.state === 'testing' || !m.online} onClick={() => onTest(m.id)} aria-label={`Test ${label} on ${m.name} now`}>
              Test now
            </Button>
            {b && b.state !== 'half-open' && (
              <Button size="sm" variant="ghost" disabled={busy} onClick={onTurnOff} aria-label={`Turn ${label} off`}>
                Turn off
              </Button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
